/**
 * useFolders Hook
 *
 * Fetches email folders with unread counts for the active account via React Query.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api, EmailFolder, EmailCreateFolderInput, EmailUpdateFolderInput } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { errorMessage } from '../types';

export function useFolders(accountId: string | undefined) {
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: queryKeys.email.folders(accountId ?? ''),
    queryFn: async () => {
      if (!accountId) throw new Error('No account selected');
      return api.listEmailFolders(accountId);
    },
    enabled: !!accountId,
  });

  const createFolder = useCallback(
    async (input: EmailCreateFolderInput) => {
      const result = await api.createEmailFolder(input);
      queryClient.invalidateQueries({ queryKey: queryKeys.email.folders(accountId ?? '') });
      return result;
    },
    [accountId, queryClient],
  );

  const updateFolder = useCallback(
    async (folderId: string, input: EmailUpdateFolderInput) => {
      if (!accountId) return;
      const result = await api.updateEmailFolder(folderId, accountId, input);
      queryClient.invalidateQueries({ queryKey: queryKeys.email.folders(accountId) });
      return result;
    },
    [accountId, queryClient],
  );

  const deleteFolder = useCallback(
    async (folderId: string) => {
      if (!accountId) return;
      await api.deleteEmailFolder(folderId, accountId);
      queryClient.invalidateQueries({ queryKey: queryKeys.email.folders(accountId) });
    },
    [accountId, queryClient],
  );

  return {
    folders: queryResult.data?.folders ?? ([] as EmailFolder[]),
    loading: queryResult.isLoading,
    error: errorMessage(queryResult.error),
    refresh: queryResult.refetch,
    createFolder,
    updateFolder,
    deleteFolder,
  };
}
